import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader, X } from 'lucide-react';
import { articleAPI } from '../lib/api';

interface SearchResult {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
}

export function SearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const handleSearch = async (q: string) => {
    setQuery(q);

    if (!q.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    setShowResults(true);

    try {
      const response = await articleAPI.search(q, 10);
      setResults(response.data.articles);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  return (
    <div className="relative w-full max-w-md">
      <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
        <Search aria-hidden="true" className="ml-3 mr-2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar artigos..."
          aria-label="Buscar artigos"
          aria-expanded={showResults}
          aria-controls="search-results"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          className="flex-1 py-2 outline-none text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-800"
        />
        {query && (
          <button
            onClick={handleClear}
            aria-label="Limpar pesquisa"
            className="px-2 py-2 text-gray-400 hover:text-gray-600 transition"
          >
            <X size={18} />
          </button>
        )}
        {loading && <Loader className="mr-3 animate-spin text-gray-400" size={18} />}
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div id="search-results" role="listbox" className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {results.length === 0 && !loading && query.trim() && (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              Nenhum resultado encontrado para "{query}"
            </div>
          )}

          {results.map((result) => (
            <Link
              key={result.id}
              to={`/article/${result.slug}`}
              role="option"
              className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm line-clamp-2">
                    {result.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-1">
                    {result.excerpt}
                  </p>
                  <span className="inline-block text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded mt-1">
                    {result.category}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
